import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/services', label: 'Services' },
    { path: '/portfolio', label: 'Portfolio' },
    { path: '/vendors', label: 'Vendors' },
    { path: '/testimonials', label: 'Testimonials' },
    { path: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'glass shadow-soft py-3' : 'bg-transparent py-5'
      }`}
    >
      <div className="container-custom">
        <nav className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="relative flex items-center gap-2" data-testid="logo-link">
            <div className={`absolute inset-0 rounded-full scale-150 transition-all duration-300 ${isScrolled ? 'bg-white/40 blur-[16px]' : 'bg-white/50 blur-[24px]'}`} />
            <img 
              src="/logo.png" 
              alt="Eden's Dawn Wedding, Events and Creations" 
              className="relative h-10 md:h-12 transition-all duration-300 drop-shadow-[0_0_8px_rgba(255,255,255,0.6)]"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={`text-sm tracking-wider uppercase transition-colors ${
                  isScrolled ? 'text-[#2C2C2C]' : 'text-white'
                } ${isActive(link.path) ? 'text-[#D4AF37]' : 'hover:text-[#D4AF37]'}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <Link to="/portal/login">
              <Button
                data-testid="client-portal-btn"
                variant="ghost"
                className={`text-sm ${isScrolled ? 'text-[#4A5D23]' : 'text-white'} hover:text-[#D4AF37]`}
              >
                Client Portal
              </Button>
            </Link>
            <Link to="/contact">
              <Button
                data-testid="inquiry-btn"
                className="bg-[#D4AF37] hover:bg-[#C5A028] text-white rounded-full px-6"
              >
                Start Planning
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className={isScrolled ? 'text-[#2C2C2C]' : 'text-white'} size={24} />
            ) : (
              <Menu className={isScrolled ? 'text-[#2C2C2C]' : 'text-white'} size={24} />
            )}
          </button>
        </nav>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden mobile-menu fixed top-0 right-0 h-screen w-72 bg-[#F5F5F0] shadow-xl z-50 p-8 ${
            isMobileMenuOpen ? 'open' : ''
          }`}
        >
          <button
            data-testid="close-mobile-menu"
            className="absolute top-4 right-4 p-2"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <X className="text-[#2C2C2C]" size={24} />
          </button>

          <div className="mt-12 flex flex-col gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                className={`text-lg font-heading ${
                  isActive(link.path) ? 'text-[#D4AF37]' : 'text-[#2C2C2C] hover:text-[#D4AF37]'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <div className="border-t border-[#E8DFD0] pt-6 mt-4 space-y-4">
              <Link to="/portal/login" onClick={() => setIsMobileMenuOpen(false)}>
                <Button
                  data-testid="mobile-portal-btn"
                  variant="outline"
                  className="w-full border-[#4A5D23] text-[#4A5D23] rounded-full"
                >
                  Client Portal
                </Button>
              </Link>
              <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>
                <Button
                  data-testid="mobile-inquiry-btn"
                  className="w-full bg-[#D4AF37] hover:bg-[#C5A028] text-white rounded-full"
                >
                  Start Planning
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Overlay */}
        {isMobileMenuOpen && (
          <div
            className="lg:hidden fixed inset-0 bg-black/50 z-40"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}
      </div>
    </header>
  );
};

export default Navbar;
